import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ref, get, set, remove, push } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import WallLayoutEditor from '../components/WallLayoutEditor';
import './Admin.css';

const Admin = () => {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dates');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Date configurations
  const [nextVisitDate, setNextVisitDate] = useState('');
  const [relationshipStartDate, setRelationshipStartDate] = useState('');

  // Sticky notes management
  const [stickyNotes, setStickyNotes] = useState([]);

  // Content management
  const [journalEntries, setJournalEntries] = useState([]);
  const [poems, setPoems] = useState([]);
  const [letters, setLetters] = useState([]);

  // Word bucket management
  const [wordBucket, setWordBucket] = useState([]);
  const [newWord, setNewWord] = useState('');

  // Connections puzzles management
  const [connectionsPuzzles, setConnectionsPuzzles] = useState([]);

  // Pinned sticky notes management
  const [pinnedNotes, setPinnedNotes] = useState([]);

  // Wordle progress management
  const [wordleProgress, setWordleProgress] = useState([]);

  // Users management
  const [allUsers, setAllUsers] = useState([]);

  // Picture frame photos management
  const [framePhotos, setFramePhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  // Voice memos management
  const [voiceMemos, setVoiceMemos] = useState([]);
  const [playingMemoId, setPlayingMemoId] = useState(null);
  const memoAudioRef = useRef(new Audio());

  // Speech bubble messages
  const [speechBubbleMessages, setSpeechBubbleMessages] = useState([]);
  const [newSpeechMessage, setNewSpeechMessage] = useState('');

  // Password management
  const [intimacyPassword, setIntimacyPassword] = useState('');
  const [henryPassword, setHenryPassword] = useState('');
  const [omiPassword, setOmiPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  // Cursor settings
  const [cursorSettings, setCursorSettings] = useState({
    globalCursorType: 'default',
    trailEnabled: true,
  });

  // Intimacy Admin Section
  const [intimacyUnlocked, setIntimacyUnlocked] = useState(false);
  const [intimacyPasswordInput, setIntimacyPasswordInput] = useState('');
  const [intimacyPasswordError, setIntimacyPasswordError] = useState('');
  const [intimacyPoems, setIntimacyPoems] = useState([]);
  const [intimacyWhispers, setIntimacyWhispers] = useState([]);
  const [complianceWall, setComplianceWall] = useState([]);
  const [freakyTasks, setFreakyTasks] = useState([]);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [vaultContents, setVaultContents] = useState([]);
  const [rewardStorage, setRewardStorage] = useState([]);
  const [favorMeter, setFavorMeter] = useState(50);
  const [tributes, setTributes] = useState([]);
  const [newProtocol, setNewProtocol] = useState('');
  const [newVaultItem, setNewVaultItem] = useState({ type: 'text', title: '', content: '' });
  const [editingFavorMeter, setEditingFavorMeter] = useState(false);
  const [newFavorValue, setNewFavorValue] = useState(50);
  const [punishments, setPunishments] = useState([]);
  const [activePunishment, setActivePunishment] = useState(null);

  // Custom confirm function
  const showConfirm = (message, onConfirm) => {
    setConfirmDialog({ message, onConfirm });
  };

  const handleConfirmYes = () => {
    if (confirmDialog?.onConfirm) {
      confirmDialog.onConfirm();
    }
    setConfirmDialog(null);
  };

  const handleConfirmNo = () => {
    setConfirmDialog(null);
  };

  // Check if user is admin (by name, case-insensitive)
  useEffect(() => {
    if (userProfile && userProfile.name?.toLowerCase() !== 'admin') {
      navigate('/home');
    }
  }, [userProfile, navigate]);

  // Set upload mode flag for FullscreenHandler - allow grace period when on photos tab
  useEffect(() => {
    // Enable upload mode when on photos tab so file picker can exit fullscreen
    window.__intimacyUploadMode = activeTab === 'photos';

    return () => {
      window.__intimacyUploadMode = false;
    };
  }, [activeTab]);

  // Load configurations
  useEffect(() => {
    if (userProfile?.name?.toLowerCase() === 'admin') {
      loadConfigurations();
    }
  }, [userProfile]);

  const loadConfigurations = async () => {
    try {
      // Load next visit date
      const visitDateRef = ref(database, 'config/nextVisitDate');
      const visitDateSnapshot = await get(visitDateRef);
      if (visitDateSnapshot.exists()) {
        const date = new Date(visitDateSnapshot.val());
        setNextVisitDate(date.toISOString().split('T')[0]);
      }

      // Load relationship start date
      const startDateRef = ref(database, 'config/relationshipStartDate');
      const startDateSnapshot = await get(startDateRef);
      if (startDateSnapshot.exists()) {
        const date = new Date(startDateSnapshot.val());
        setRelationshipStartDate(date.toISOString().split('T')[0]);
      }

      // Load sticky notes
      const notesRef = ref(database, 'desk/stickyNotes');
      const notesSnapshot = await get(notesRef);
      if (notesSnapshot.exists()) {
        const notesData = notesSnapshot.val();
        const notesArray = Object.entries(notesData).map(([id, note]) => ({
          id,
          ...note,
        }));
        setStickyNotes(notesArray);
      }

      // Load journal entries
      const journalRef = ref(database, 'journal/entries');
      const journalSnapshot = await get(journalRef);
      if (journalSnapshot.exists()) {
        const journalData = journalSnapshot.val();
        const entriesArray = Object.entries(journalData).map(([id, entry]) => ({
          id,
          ...entry,
        }));
        // Sort by creation date (newest first)
        entriesArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setJournalEntries(entriesArray);
      } else {
        setJournalEntries([]);
      }

      // Load poems
      const poemsRef = ref(database, 'poetry/entries');
      const poemsSnapshot = await get(poemsRef);
      if (poemsSnapshot.exists()) {
        const poemsData = poemsSnapshot.val();
        const poemsArray = Object.entries(poemsData).map(([id, poem]) => ({
          id,
          ...poem,
        }));
        // Sort by creation date (newest first)
        poemsArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setPoems(poemsArray);
      } else {
        setPoems([]);
      }

      // Load letters
      const lettersRef = ref(database, 'letters/entries');
      const lettersSnapshot = await get(lettersRef);
      if (lettersSnapshot.exists()) {
        const lettersData = lettersSnapshot.val();
        const lettersArray = Object.entries(lettersData).map(([id, letter]) => ({
          id,
          ...letter,
        }));
        // Sort by creation date (newest first)
        lettersArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setLetters(lettersArray);
      } else {
        setLetters([]);
      }

      // Load word bucket
      const wordBucketRef = ref(database, 'puzzles/wordBucket');
      const wordBucketSnapshot = await get(wordBucketRef);
      if (wordBucketSnapshot.exists()) {
        const wordsData = wordBucketSnapshot.val();
        const wordsArray = Object.entries(wordsData).map(([id, word]) => ({
          id,
          ...word,
        }));
        // Sort alphabetically
        wordsArray.sort((a, b) => a.word.localeCompare(b.word));
        setWordBucket(wordsArray);
      } else {
        setWordBucket([]);
      }

      // Load frame photos
      const photosRef = ref(database, 'photos/frame');
      const photosSnapshot = await get(photosRef);
      if (photosSnapshot.exists()) {
        const photosData = photosSnapshot.val();
        const photosArray = Object.entries(photosData).map(([id, photo]) => ({
          id,
          ...photo,
        }));
        // Sort by creation date (newest first)
        photosArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setFramePhotos(photosArray);
      } else {
        setFramePhotos([]);
      }

      // Load voice memos
      const memosRef = ref(database, 'voiceMemos');
      const memosSnapshot = await get(memosRef);
      if (memosSnapshot.exists()) {
        const memosData = memosSnapshot.val();
        const memosArray = Object.entries(memosData).map(([id, memo]) => ({
          id,
          ...memo,
        }));
        memosArray.sort((a, b) => b.timestamp - a.timestamp);
        setVoiceMemos(memosArray);
      } else {
        setVoiceMemos([]);
      }

      // Load speech bubble messages
      const messagesRef = ref(database, 'config/voiceMemoMessages');
      const messagesSnapshot = await get(messagesRef);
      if (messagesSnapshot.exists()) {
        const messagesData = messagesSnapshot.val();
        if (Array.isArray(messagesData)) {
          setSpeechBubbleMessages(messagesData);
        } else {
          setSpeechBubbleMessages(Object.values(messagesData));
        }
      } else {
        setSpeechBubbleMessages([]);
      }

      // Load intimacy password
      const intimacyPwRef = ref(database, 'settings/intimacyPassword');
      const intimacyPwSnapshot = await get(intimacyPwRef);
      if (intimacyPwSnapshot.exists()) {
        setIntimacyPassword(intimacyPwSnapshot.val());
      } else {
        setIntimacyPassword('intimate'); // Default
      }

      // Load connections puzzles
      const puzzlesRef = ref(database, 'puzzles/connections');
      const puzzlesSnapshot = await get(puzzlesRef);
      if (puzzlesSnapshot.exists()) {
        const puzzlesData = puzzlesSnapshot.val();
        const puzzlesArray = Object.entries(puzzlesData).map(([id, puzzle]) => ({
          id,
          ...puzzle,
        }));
        // Sort by creation date (newest first)
        puzzlesArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setConnectionsPuzzles(puzzlesArray);
      } else {
        setConnectionsPuzzles([]);
      }

      // Load pinned sticky notes
      const pinnedRef = ref(database, 'wall/pinnedNotes');
      const pinnedSnapshot = await get(pinnedRef);
      if (pinnedSnapshot.exists()) {
        const pinnedData = pinnedSnapshot.val();
        const pinnedArray = Object.entries(pinnedData).map(([id, note]) => ({
          id,
          ...note,
        }));
        // Sort by creation date (newest first)
        pinnedArray.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setPinnedNotes(pinnedArray);
      } else {
        setPinnedNotes([]);
      }

      // Load wordle progress (all dates)
      const wordleRef = ref(database, 'puzzles/wordle');
      const wordleSnapshot = await get(wordleRef);
      if (wordleSnapshot.exists()) {
        const wordleData = wordleSnapshot.val();
        const wordleArray = Object.entries(wordleData).map(([date, data]) => ({
          date,
          word: data.word,
          progress: data.progress || {},
        }));
        // Sort by date (newest first)
        wordleArray.sort((a, b) => new Date(b.date) - new Date(a.date));
        setWordleProgress(wordleArray);
      } else {
        setWordleProgress([]);
      }

      // Load all users from database with activity info
      const usersRef = ref(database, 'users');
      const usersSnapshot = await get(usersRef);
      if (usersSnapshot.exists()) {
        const usersData = usersSnapshot.val();
        const usersArray = [];

        for (const [uid, userData] of Object.entries(usersData)) {
          // Check for associated data to help identify which account is active
          let hasJournalEntries = false;
          let hasPoems = false;
          let hasStickyNotes = false;
          let hasWordleProgress = false;

          // Check journal entries
          const journalData = journalSnapshot?.exists() ? journalSnapshot.val() : {};
          hasJournalEntries = Object.values(journalData).some(entry => entry.createdByUid === uid);

          // Check poems
          const poemsData = poemsSnapshot?.exists() ? poemsSnapshot.val() : {};
          hasPoems = Object.values(poemsData).some(poem => poem.createdByUid === uid);

          // Check sticky notes
          const notesData = notesSnapshot?.exists() ? notesSnapshot.val() : {};
          hasStickyNotes = Object.values(notesData).some(note => note.createdByUid === uid);

          // Check wordle progress
          const wordleData = wordleSnapshot?.exists() ? wordleSnapshot.val() : {};
          for (const dateData of Object.values(wordleData)) {
            if (dateData.progress && dateData.progress[uid]) {
              hasWordleProgress = true;
              break;
            }
          }

          usersArray.push({
            uid,
            ...userData,
            hasActivity: hasJournalEntries || hasPoems || hasStickyNotes || hasWordleProgress,
            activityDetails: {
              journal: hasJournalEntries,
              poems: hasPoems,
              stickyNotes: hasStickyNotes,
              wordle: hasWordleProgress,
            }
          });
        }

        // Sort: users with activity first, then by name
        usersArray.sort((a, b) => {
          if (a.hasActivity !== b.hasActivity) return a.hasActivity ? -1 : 1;
          return (a.name || '').localeCompare(b.name || '');
        });

        setAllUsers(usersArray);
      } else {
        setAllUsers([]);
      }

      // Load cursor settings
      const cursorRef = ref(database, 'settings/cursor');
      const cursorSnapshot = await get(cursorRef);
      if (cursorSnapshot.exists()) {
        setCursorSettings(cursorSnapshot.val());
      } else {
        setCursorSettings({
          globalCursorType: 'default',
          trailEnabled: true,
        });
      }
    } catch (error) {
      console.error('Error loading configurations:', error);
      showMessage('Error loading configurations', 'error');
    }
  };

  const showMessage = (msg, type = 'success') => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(''), 3000);
  };

  // Load intimacy data (only when unlocked)
  const loadIntimacyData = async () => {
    try {
      // Load intimacy poems (Eros mode)
      const erosPoemsRef = ref(database, 'intimacy/eros/poems');
      const erosPoemsSnapshot = await get(erosPoemsRef);
      if (erosPoemsSnapshot.exists()) {
        const poemsData = erosPoemsSnapshot.val();
        const poemsArray = Object.entries(poemsData).map(([id, poem]) => ({
          id,
          ...poem,
        }));
        poemsArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setIntimacyPoems(poemsArray);
      } else {
        setIntimacyPoems([]);
      }

      // Load whispers (Eros mode)
      const whispersRef = ref(database, 'intimacy/eros/whispers');
      const whispersSnapshot = await get(whispersRef);
      if (whispersSnapshot.exists()) {
        const whispersData = whispersSnapshot.val();
        const whispersArray = Object.entries(whispersData).map(([id, whisper]) => ({
          id,
          ...whisper,
        }));
        whispersArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setIntimacyWhispers(whispersArray);
      } else {
        setIntimacyWhispers([]);
      }

      // Load compliance wall
      const complianceRef = ref(database, 'intimacy/freaky/compliance');
      const complianceSnapshot = await get(complianceRef);
      if (complianceSnapshot.exists()) {
        const complianceData = complianceSnapshot.val();
        const complianceArray = Object.entries(complianceData).map(([id, item]) => ({
          id,
          ...item,
        }));
        complianceArray.sort((a, b) => new Date(b.uploadedAt || b.approvedAt || 0) - new Date(a.uploadedAt || a.approvedAt || 0));
        setComplianceWall(complianceArray);
      } else {
        setComplianceWall([]);
      }

      // Load tasks
      const tasksRef = ref(database, 'intimacy/freaky/tasks');
      const tasksSnapshot = await get(tasksRef);
      if (tasksSnapshot.exists()) {
        const tasksData = tasksSnapshot.val();
        const tasksArray = Object.entries(tasksData).map(([id, task]) => ({
          id,
          ...task,
        }));
        tasksArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setFreakyTasks(tasksArray);
      } else {
        setFreakyTasks([]);
      }

      // Load archived tasks
      const archivedRef = ref(database, 'intimacy/freaky/archivedTasks');
      const archivedSnapshot = await get(archivedRef);
      if (archivedSnapshot.exists()) {
        const archivedData = archivedSnapshot.val();
        const archivedArray = Object.entries(archivedData).map(([id, task]) => ({
          id,
          ...task,
        }));
        archivedArray.sort((a, b) => new Date(b.archivedAt || b.createdAt) - new Date(a.archivedAt || a.createdAt));
        setArchivedTasks(archivedArray);
      } else {
        setArchivedTasks([]);
      }

      // Load protocols
      const protocolsRef = ref(database, 'intimacy/freaky/protocols');
      const protocolsSnapshot = await get(protocolsRef);
      if (protocolsSnapshot.exists()) {
        const protocolsData = protocolsSnapshot.val();
        const protocolsArray = Object.entries(protocolsData).map(([id, protocol]) => ({
          id,
          ...protocol,
        }));
        setProtocols(protocolsArray);
      } else {
        setProtocols([]);
      }

      // Load vault contents
      const vaultRef = ref(database, 'intimacy/freaky/vaultContents');
      const vaultSnapshot = await get(vaultRef);
      if (vaultSnapshot.exists()) {
        const vaultData = vaultSnapshot.val();
        const vaultArray = Object.entries(vaultData).map(([id, item]) => ({
          id,
          ...item,
        }));
        vaultArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setVaultContents(vaultArray);
      } else {
        setVaultContents([]);
      }

      // Load reward storage
      const rewardRef = ref(database, 'intimacy/freaky/rewardStorage');
      const rewardSnapshot = await get(rewardRef);
      if (rewardSnapshot.exists()) {
        const rewardData = rewardSnapshot.val();
        const rewardArray = Object.entries(rewardData).map(([id, item]) => ({
          id,
          ...item,
        }));
        rewardArray.sort((a, b) => new Date(b.unlockedAt || b.createdAt) - new Date(a.unlockedAt || a.createdAt));
        setRewardStorage(rewardArray);
      } else {
        setRewardStorage([]);
      }

      // Load favor meter
      const favorRef = ref(database, 'intimacy/freaky/favorMeter');
      const favorSnapshot = await get(favorRef);
      if (favorSnapshot.exists()) {
        setFavorMeter(favorSnapshot.val());
        setNewFavorValue(favorSnapshot.val());
      } else {
        setFavorMeter(50);
        setNewFavorValue(50);
      }

      // Load punishments pool
      const punishmentsRef = ref(database, 'intimacy/freaky/punishments');
      const punishmentsSnapshot = await get(punishmentsRef);
      if (punishmentsSnapshot.exists()) {
        const punishmentsData = punishmentsSnapshot.val();
        const punishmentsArray = Object.entries(punishmentsData).map(([id, punishment]) => ({
          id,
          ...punishment,
        }));
        setPunishments(punishmentsArray);
      } else {
        setPunishments([]);
      }

      // Load active punishment
      const activePunishmentRef = ref(database, 'intimacy/freaky/activePunishment');
      const activePunishmentSnapshot = await get(activePunishmentRef);
      if (activePunishmentSnapshot.exists()) {
        setActivePunishment(activePunishmentSnapshot.val());
      } else {
        setActivePunishment(null);
      }

      // Load tributes
      const tributesRef = ref(database, 'intimacy/freaky/tributes');
      const tributesSnapshot = await get(tributesRef);
      if (tributesSnapshot.exists()) {
        const tributesData = tributesSnapshot.val();
        const tributesArray = Object.entries(tributesData).map(([id, tribute]) => ({
          id,
          ...tribute,
        }));
        tributesArray.sort((a, b) => new Date(b.submittedAt || b.createdAt) - new Date(a.submittedAt || a.createdAt));
        setTributes(tributesArray);
      } else {
        setTributes([]);
      }
    } catch (error) {
      console.error('Error loading intimacy data:', error);
      showMessage('Error loading intimacy data', 'error');
    }
  };

  // Handle unlocking intimacy admin section
  const handleUnlockIntimacy = async () => {
    if (intimacyPasswordInput === intimacyPassword) {
      setIntimacyUnlocked(true);
      setIntimacyPasswordError('');
      setIntimacyPasswordInput('');
      await loadIntimacyData();
    } else {
      setIntimacyPasswordError('Incorrect password');
    }
  };

  // Intimacy Poems CRUD
  const handleDeleteIntimacyPoem = async (poemId) => {
    showConfirm('Are you sure you want to delete this intimacy poem?', async () => {
      setLoading(true);
      try {
        const poemRef = ref(database, `intimacy/eros/poems/${poemId}`);
        await remove(poemRef);
        setIntimacyPoems(intimacyPoems.filter(poem => poem.id !== poemId));
        showMessage('Intimacy poem deleted successfully!');
      } catch (error) {
        console.error('Error deleting intimacy poem:', error);
        showMessage('Error deleting poem', 'error');
      }
      setLoading(false);
    });
  };

  const handleDeleteWhisper = async (whisperId) => {
    showConfirm('Are you sure you want to delete this whisper?', async () => {
      setLoading(true);
      try {
        const whisperRef = ref(database, `intimacy/eros/whispers/${whisperId}`);
        await remove(whisperRef);
        setIntimacyWhispers(intimacyWhispers.filter(w => w.id !== whisperId));
        showMessage('Whisper deleted successfully!');
      } catch (error) {
        console.error('Error deleting whisper:', error);
        showMessage('Error deleting whisper', 'error');
      }
      setLoading(false);
    });
  };

  // Compliance Wall CRUD
  const handleDeleteComplianceItem = async (itemId) => {
    showConfirm('Are you sure you want to delete this compliance wall image?', async () => {
      setLoading(true);
      try {
        const itemRef = ref(database, `intimacy/freaky/compliance/${itemId}`);
        await remove(itemRef);
        setComplianceWall(complianceWall.filter(item => item.id !== itemId));
        showMessage('Compliance image deleted successfully!');
      } catch (error) {
        console.error('Error deleting compliance item:', error);
        showMessage('Error deleting compliance item', 'error');
      }
      setLoading(false);
    });
  };

  const handleClearComplianceWall = async () => {
    showConfirm('Are you sure you want to clear the ENTIRE compliance wall? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const complianceRef = ref(database, 'intimacy/freaky/compliance');
        await remove(complianceRef);
        setComplianceWall([]);
        showMessage('Compliance wall cleared successfully!');
      } catch (error) {
        console.error('Error clearing compliance wall:', error);
        showMessage('Error clearing compliance wall', 'error');
      }
      setLoading(false);
    });
  };

  // Tasks CRUD
  const handleDeleteTask = async (taskId) => {
    showConfirm('Are you sure you want to delete this task?', async () => {
      setLoading(true);
      try {
        const taskRef = ref(database, `intimacy/freaky/tasks/${taskId}`);
        await remove(taskRef);
        setFreakyTasks(freakyTasks.filter(task => task.id !== taskId));
        showMessage('Task deleted successfully!');
      } catch (error) {
        console.error('Error deleting task:', error);
        showMessage('Error deleting task', 'error');
      }
      setLoading(false);
    });
  };

  const handleDeleteArchivedTask = async (taskId) => {
    showConfirm('Are you sure you want to delete this archived task?', async () => {
      setLoading(true);
      try {
        const taskRef = ref(database, `intimacy/freaky/archivedTasks/${taskId}`);
        await remove(taskRef);
        setArchivedTasks(archivedTasks.filter(task => task.id !== taskId));
        showMessage('Archived task deleted successfully!');
      } catch (error) {
        console.error('Error deleting archived task:', error);
        showMessage('Error deleting archived task', 'error');
      }
      setLoading(false);
    });
  };

  const handleClearAllTasks = async () => {
    showConfirm('Are you sure you want to delete ALL tasks? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const tasksRef = ref(database, 'intimacy/freaky/tasks');
        await remove(tasksRef);
        setFreakyTasks([]);
        showMessage('All tasks cleared successfully!');
      } catch (error) {
        console.error('Error clearing tasks:', error);
        showMessage('Error clearing tasks', 'error');
      }
      setLoading(false);
    });
  };

  const handleClearArchivedTasks = async () => {
    showConfirm('Are you sure you want to delete ALL archived tasks? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const archivedRef = ref(database, 'intimacy/freaky/archivedTasks');
        await remove(archivedRef);
        setArchivedTasks([]);
        showMessage('Archived tasks cleared successfully!');
      } catch (error) {
        console.error('Error clearing archived tasks:', error);
        showMessage('Error clearing archived tasks', 'error');
      }
      setLoading(false);
    });
  };

  // Protocols CRUD
  const handleAddProtocol = async () => {
    if (!newProtocol.trim()) {
      showMessage('Please enter a protocol', 'error');
      return;
    }
    setLoading(true);
    try {
      const protocolsRef = ref(database, 'intimacy/freaky/protocols');
      const newProtocolRef = push(protocolsRef);
      await set(newProtocolRef, {
        text: newProtocol.trim(),
        createdAt: new Date().toISOString(),
      });
      setProtocols([...protocols, { id: newProtocolRef.key, text: newProtocol.trim() }]);
      setNewProtocol('');
      showMessage('Protocol added successfully!');
    } catch (error) {
      console.error('Error adding protocol:', error);
      showMessage('Error adding protocol', 'error');
    }
    setLoading(false);
  };

  const handleDeleteProtocol = async (protocolId) => {
    showConfirm('Are you sure you want to delete this protocol?', async () => {
      setLoading(true);
      try {
        const protocolRef = ref(database, `intimacy/freaky/protocols/${protocolId}`);
        await remove(protocolRef);
        setProtocols(protocols.filter(p => p.id !== protocolId));
        showMessage('Protocol deleted successfully!');
      } catch (error) {
        console.error('Error deleting protocol:', error);
        showMessage('Error deleting protocol', 'error');
      }
      setLoading(false);
    });
  };

  const handleClearProtocols = async () => {
    showConfirm('Are you sure you want to delete ALL protocols? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const protocolsRef = ref(database, 'intimacy/freaky/protocols');
        await remove(protocolsRef);
        setProtocols([]);
        showMessage('All protocols cleared successfully!');
      } catch (error) {
        console.error('Error clearing protocols:', error);
        showMessage('Error clearing protocols', 'error');
      }
      setLoading(false);
    });
  };

  // Vault CRUD
  const handleAddVaultItem = async () => {
    if (!newVaultItem.title.trim() || !newVaultItem.content.trim()) {
      showMessage('Please enter both title and content', 'error');
      return;
    }
    setLoading(true);
    try {
      const vaultRef = ref(database, 'intimacy/freaky/vaultContents');
      const newItemRef = push(vaultRef);
      const itemData = {
        type: newVaultItem.type,
        title: newVaultItem.title.trim(),
        content: newVaultItem.content.trim(),
        createdAt: new Date().toISOString(),
      };
      await set(newItemRef, itemData);
      setVaultContents([{ id: newItemRef.key, ...itemData }, ...vaultContents]);
      setNewVaultItem({ type: 'text', title: '', content: '' });
      showMessage('Vault item added successfully!');
    } catch (error) {
      console.error('Error adding vault item:', error);
      showMessage('Error adding vault item', 'error');
    }
    setLoading(false);
  };

  const handleDeleteVaultItem = async (itemId) => {
    showConfirm('Are you sure you want to delete this vault item?', async () => {
      setLoading(true);
      try {
        const itemRef = ref(database, `intimacy/freaky/vaultContents/${itemId}`);
        await remove(itemRef);
        setVaultContents(vaultContents.filter(item => item.id !== itemId));
        showMessage('Vault item deleted successfully!');
      } catch (error) {
        console.error('Error deleting vault item:', error);
        showMessage('Error deleting vault item', 'error');
      }
      setLoading(false);
    });
  };

  const handleClearVault = async () => {
    showConfirm('Are you sure you want to clear the ENTIRE vault? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const vaultRef = ref(database, 'intimacy/freaky/vaultContents');
        await remove(vaultRef);
        setVaultContents([]);
        showMessage('Vault cleared successfully!');
      } catch (error) {
        console.error('Error clearing vault:', error);
        showMessage('Error clearing vault', 'error');
      }
      setLoading(false);
    });
  };

  // Reward Storage
  const handleDeleteRewardItem = async (itemId) => {
    showConfirm('Are you sure you want to delete this reward from Omi\'s storage?', async () => {
      setLoading(true);
      try {
        const itemRef = ref(database, `intimacy/freaky/rewardStorage/${itemId}`);
        await remove(itemRef);
        setRewardStorage(rewardStorage.filter(item => item.id !== itemId));
        showMessage('Reward deleted successfully!');
      } catch (error) {
        console.error('Error deleting reward:', error);
        showMessage('Error deleting reward', 'error');
      }
      setLoading(false);
    });
  };

  const handleClearRewardStorage = async () => {
    showConfirm('Are you sure you want to clear Omi\'s ENTIRE reward storage? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const rewardRef = ref(database, 'intimacy/freaky/rewardStorage');
        await remove(rewardRef);
        setRewardStorage([]);
        showMessage('Reward storage cleared successfully!');
      } catch (error) {
        console.error('Error clearing reward storage:', error);
        showMessage('Error clearing reward storage', 'error');
      }
      setLoading(false);
    });
  };

  // Favor Meter
  const handleUpdateFavorMeter = async () => {
    setLoading(true);
    try {
      const favorRef = ref(database, 'intimacy/freaky/favorMeter');

      // If setting below 0, trigger punishment protocol
      if (newFavorValue < 0) {
        // Check if there's already an active punishment
        if (activePunishment) {
          showMessage('Punishment already active. Complete it first before triggering another.', 'error');
          setLoading(false);
          return;
        }

        // Check if there are punishments in the pool
        if (punishments.length === 0) {
          showMessage('No punishments in pool. Add punishments first before setting favor below 0.', 'error');
          setLoading(false);
          return;
        }

        // Select a random punishment from the pool
        const randomIndex = Math.floor(Math.random() * punishments.length);
        const selectedPunishment = punishments[randomIndex];

        // Activate the punishment
        const activePunishmentRef = ref(database, 'intimacy/freaky/activePunishment');
        await set(activePunishmentRef, {
          ...selectedPunishment,
          activatedAt: new Date().toISOString(),
          triggerPenalty: Math.abs(newFavorValue)
        });
        setActivePunishment({
          ...selectedPunishment,
          activatedAt: new Date().toISOString()
        });

        // Set favor meter to 0 (not negative)
        await set(favorRef, 0);
        setFavorMeter(0);
        setNewFavorValue(0);
        setEditingFavorMeter(false);
        showMessage(`Punishment activated: "${selectedPunishment.text}". Favor set to 0.`);
      } else {
        // Normal update
        await set(favorRef, newFavorValue);
        setFavorMeter(newFavorValue);
        setEditingFavorMeter(false);
        showMessage('Favor meter updated successfully!');
      }
    } catch (error) {
      console.error('Error updating favor meter:', error);
      showMessage('Error updating favor meter', 'error');
    }
    setLoading(false);
  };

  // Tributes
  const handleDeleteTribute = async (tributeId) => {
    showConfirm('Are you sure you want to delete this tribute?', async () => {
      setLoading(true);
      try {
        const tributeRef = ref(database, `intimacy/freaky/tributes/${tributeId}`);
        await remove(tributeRef);
        setTributes(tributes.filter(t => t.id !== tributeId));
        showMessage('Tribute deleted successfully!');
      } catch (error) {
        console.error('Error deleting tribute:', error);
        showMessage('Error deleting tribute', 'error');
      }
      setLoading(false);
    });
  };

  const handleClearTributes = async () => {
    showConfirm('Are you sure you want to clear ALL tributes? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const tributesRef = ref(database, 'intimacy/freaky/tributes');
        await remove(tributesRef);
        setTributes([]);
        showMessage('Tributes cleared successfully!');
      } catch (error) {
        console.error('Error clearing tributes:', error);
        showMessage('Error clearing tributes', 'error');
      }
      setLoading(false);
    });
  };

  const handleSaveNextVisitDate = async () => {
    if (!nextVisitDate) return;

    setLoading(true);
    try {
      const visitDateRef = ref(database, 'config/nextVisitDate');
      await set(visitDateRef, new Date(nextVisitDate).toISOString());
      showMessage('Next visit date updated successfully!');
    } catch (error) {
      console.error('Error saving next visit date:', error);
      showMessage('Error saving next visit date', 'error');
    }
    setLoading(false);
  };

  const handleSaveRelationshipStartDate = async () => {
    if (!relationshipStartDate) return;

    setLoading(true);
    try {
      const startDateRef = ref(database, 'config/relationshipStartDate');
      await set(startDateRef, new Date(relationshipStartDate).toISOString());
      showMessage('Relationship start date updated successfully!');
    } catch (error) {
      console.error('Error saving relationship start date:', error);
      showMessage('Error saving relationship start date', 'error');
    }
    setLoading(false);
  };

  const handleSaveCursorSettings = async () => {
    setLoading(true);
    try {
      const cursorRef = ref(database, 'settings/cursor');
      await set(cursorRef, cursorSettings);
      showMessage('Cursor settings saved successfully!');
    } catch (error) {
      console.error('Error saving cursor settings:', error);
      showMessage('Error saving cursor settings', 'error');
    }
    setLoading(false);
  };

  const handleDeleteStickyNote = async (noteId) => {
    showConfirm('Are you sure you want to delete this sticky note?', async () => {
      setLoading(true);
      try {
        const noteRef = ref(database, `desk/stickyNotes/${noteId}`);
        await remove(noteRef);
        setStickyNotes(stickyNotes.filter((note) => note.id !== noteId));
        showMessage('Sticky note deleted successfully!');
      } catch (error) {
        console.error('Error deleting sticky note:', error);
        showMessage('Error deleting sticky note', 'error');
      }
      setLoading(false);
    });
  };

  const handleClearEmptyStickyNotes = async () => {
    const emptyNotes = stickyNotes.filter(note => !note.text || note.text.trim() === '');

    if (emptyNotes.length === 0) {
      showMessage('No empty sticky notes to clear', 'info');
      return;
    }

    showConfirm(`Are you sure you want to delete ${emptyNotes.length} empty sticky note(s)?`, async () => {
      setLoading(true);
      try {
        // Delete each empty note
        for (const note of emptyNotes) {
          const noteRef = ref(database, `desk/stickyNotes/${note.id}`);
          await remove(noteRef);
        }

        // Update local state
        setStickyNotes(stickyNotes.filter(note => note.text && note.text.trim() !== ''));
        showMessage(`${emptyNotes.length} empty sticky note(s) cleared successfully!`);
      } catch (error) {
        console.error('Error clearing empty sticky notes:', error);
        showMessage('Error clearing empty sticky notes', 'error');
      }
      setLoading(false);
    });
  };

  const handleClearAllStickyNotes = async () => {
    showConfirm('Are you sure you want to delete ALL sticky notes? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const notesRef = ref(database, 'desk/stickyNotes');
        await remove(notesRef);
        setStickyNotes([]);
        showMessage('All sticky notes cleared successfully!');
      } catch (error) {
        console.error('Error clearing sticky notes:', error);
        showMessage('Error clearing sticky notes', 'error');
      }
      setLoading(false);
    });
  };

  const handleDeleteJournalEntry = async (entryId) => {
    showConfirm('Are you sure you want to delete this journal entry?', async () => {
      setLoading(true);
      try {
        const entryRef = ref(database, `journal/entries/${entryId}`);
        await remove(entryRef);
        setJournalEntries(journalEntries.filter(entry => entry.id !== entryId));
        showMessage('Journal entry deleted successfully!');
      } catch (error) {
        console.error('Error deleting journal entry:', error);
        showMessage('Error deleting journal entry', 'error');
      }
      setLoading(false);
    });
  };

  const handleDeletePoem = async (poemId) => {
    showConfirm('Are you sure you want to delete this poem?', async () => {
      setLoading(true);
      try {
        const poemRef = ref(database, `poetry/entries/${poemId}`);
        await remove(poemRef);
        setPoems(poems.filter(poem => poem.id !== poemId));
        showMessage('Poem deleted successfully!');
      } catch (error) {
        console.error('Error deleting poem:', error);
        showMessage('Error deleting poem', 'error');
      }
      setLoading(false);
    });
  };

  const handleDeleteLetter = async (letterId) => {
    showConfirm('Are you sure you want to delete this letter?', async () => {
      setLoading(true);
      try {
        const letterRef = ref(database, `letters/entries/${letterId}`);
        await remove(letterRef);
        setLetters(letters.filter(letter => letter.id !== letterId));
        showMessage('Letter deleted successfully!');
      } catch (error) {
        console.error('Error deleting letter:', error);
        showMessage('Error deleting letter', 'error');
      }
      setLoading(false);
    });
  };

  const handleAddWord = async () => {
    if (!newWord.trim()) {
      showMessage('Please enter a word', 'error');
      return;
    }

    const trimmedWord = newWord.trim().toUpperCase();

    // Validate 5-letter word
    if (trimmedWord.length !== 5) {
      showMessage('Word must be exactly 5 letters for Wordle!', 'error');
      return;
    }

    // Validate only letters
    if (!/^[A-Z]+$/.test(trimmedWord)) {
      showMessage('Word must contain only letters!', 'error');
      return;
    }

    // Check for duplicates
    if (wordBucket.some(w => w.word === trimmedWord)) {
      showMessage('This word already exists in the bucket', 'error');
      return;
    }

    setLoading(true);
    try {
      const wordBucketRef = ref(database, 'puzzles/wordBucket');
      const newWordRef = ref(database, `puzzles/wordBucket/${Date.now()}`);
      await set(newWordRef, {
        word: trimmedWord,
        createdAt: new Date().toISOString(),
        createdBy: userProfile.name,
      });

      // Reload word bucket
      await loadConfigurations();
      setNewWord('');
      showMessage('Word added successfully!');
    } catch (error) {
      console.error('Error adding word:', error);
      showMessage('Error adding word', 'error');
    }
    setLoading(false);
  };

  const handleDeleteWord = async (wordId) => {
    showConfirm('Are you sure you want to delete this word?', async () => {
      setLoading(true);
      try {
        const wordRef = ref(database, `puzzles/wordBucket/${wordId}`);
        await remove(wordRef);
        setWordBucket(wordBucket.filter(w => w.id !== wordId));
        showMessage('Word deleted successfully!');
      } catch (error) {
        console.error('Error deleting word:', error);
        showMessage('Error deleting word', 'error');
      }
      setLoading(false);
    });
  };

  const handleClearWordBucket = async () => {
    showConfirm('Are you sure you want to delete ALL words from the bucket? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const wordBucketRef = ref(database, 'puzzles/wordBucket');
        await remove(wordBucketRef);
        setWordBucket([]);
        showMessage('Word bucket cleared successfully!');
      } catch (error) {
        console.error('Error clearing word bucket:', error);
        showMessage('Error clearing word bucket', 'error');
      }
      setLoading(false);
    });
  };

  // Re-enter fullscreen after file upload
  const reenterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  };

  // Photo upload handler - using base64 encoding (no Firebase Storage needed)
  const handlePhotoUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      setPhotoUploading(false);
      return;
    }

    setPhotoUploading(true);
    console.log('Starting photo upload, files:', files.length);

    try {
      let uploadedCount = 0;
      let failedCount = 0;

      for (const file of files) {
        try {
          console.log('Processing file:', file.name, 'type:', file.type, 'size:', file.size);

          // Validate file type
          if (!file.type.startsWith('image/')) {
            showMessage(`${file.name} is not an image file`, 'error');
            failedCount++;
            continue;
          }

          // Validate file size (max 5MB for base64)
          if (file.size > 5 * 1024 * 1024) {
            showMessage(`${file.name} is too large (max 5MB)`, 'error');
            failedCount++;
            continue;
          }

          // Compress and resize image before converting to base64
          const compressedBase64 = await new Promise((resolve, reject) => {
            const img = new Image();
            const reader = new FileReader();

            reader.onload = (e) => {
              img.src = e.target.result;
            };

            img.onload = () => {
              // Create canvas for compression
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');

              // Calculate new dimensions (max 800px width, maintain aspect ratio)
              const maxWidth = 800;
              let width = img.width;
              let height = img.height;

              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }

              canvas.width = width;
              canvas.height = height;

              // Draw and compress image
              ctx.drawImage(img, 0, 0, width, height);

              // Convert to base64 with quality compression (0.8 = 80% quality)
              const compressedBase64 = canvas.toDataURL(file.type, 0.8);
              resolve(compressedBase64);
            };

            img.onerror = reject;
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          console.log('Converted and compressed to base64, saving to database...');

          // Save photo data to database (using base64 instead of Storage)
          const photosRef = ref(database, 'photos/frame');
          const newPhotoRef = push(photosRef);

          await set(newPhotoRef, {
            url: compressedBase64,  // Compressed base64 string
            originalName: file.name,
            mediaType: file.type,
            caption: '',
            createdAt: new Date().toISOString(),
            createdBy: userProfile.name,
          });

          console.log('Saved to database successfully');
          uploadedCount++;
        } catch (fileError) {
          console.error(`Error uploading ${file.name}:`, fileError);
          showMessage(`Failed to upload ${file.name}: ${fileError.message || 'Unknown error'}`, 'error');
          failedCount++;
        }
      }

      // Reload photos if any succeeded
      if (uploadedCount > 0) {
        await loadConfigurations();
        showMessage(`${uploadedCount} photo(s) uploaded successfully!${failedCount > 0 ? ` (${failedCount} failed)` : ''}`);
        // Re-enter fullscreen after successful upload
        reenterFullscreen();
      } else if (failedCount > 0) {
        showMessage(`All uploads failed. Check console for details.`, 'error');
      }
    } catch (error) {
      console.error('Critical error in photo upload:', error);
      showMessage(`Critical error: ${error.message}`, 'error');
    } finally {
      setPhotoUploading(false);
      // Reset file input
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    showConfirm('Are you sure you want to delete this photo?', async () => {
      setLoading(true);
      try {
        // Delete from Database (no Storage to delete from)
        const photoRef = ref(database, `photos/frame/${photoId}`);
        await remove(photoRef);

        setFramePhotos(framePhotos.filter(p => p.id !== photoId));
        showMessage('Photo deleted successfully!');
      } catch (error) {
        console.error('Error deleting photo:', error);
        showMessage('Error deleting photo', 'error');
      }
      setLoading(false);
    });
  };

  // Voice memo functions
  const formatMemoTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayMemo = (memo) => {
    if (playingMemoId === memo.id) {
      memoAudioRef.current.pause();
      memoAudioRef.current.currentTime = 0;
      setPlayingMemoId(null);
    } else {
      memoAudioRef.current.src = memo.audioData;
      memoAudioRef.current.play();
      setPlayingMemoId(memo.id);
      memoAudioRef.current.onended = () => setPlayingMemoId(null);
    }
  };

  const handleDeleteMemo = async (memoId, memoTitle) => {
    showConfirm(`Are you sure you want to delete "${memoTitle}"?`, async () => {
      setLoading(true);
      try {
        const memoRef = ref(database, `voiceMemos/${memoId}`);
        await remove(memoRef);
        setVoiceMemos(voiceMemos.filter(m => m.id !== memoId));
        if (playingMemoId === memoId) {
          memoAudioRef.current.pause();
          setPlayingMemoId(null);
        }
        showMessage('Voice memo deleted successfully!');
      } catch (error) {
        console.error('Error deleting memo:', error);
        showMessage('Error deleting voice memo', 'error');
      }
      setLoading(false);
    });
  };

  // Speech bubble message functions
  const handleAddSpeechMessage = async () => {
    if (!newSpeechMessage.trim()) return;
    setLoading(true);
    try {
      const updatedMessages = [...speechBubbleMessages, newSpeechMessage.trim()];
      const messagesRef = ref(database, 'config/voiceMemoMessages');
      await set(messagesRef, updatedMessages);
      setSpeechBubbleMessages(updatedMessages);
      setNewSpeechMessage('');
      showMessage('Speech bubble message added!');
    } catch (error) {
      console.error('Error adding message:', error);
      showMessage('Error adding message', 'error');
    }
    setLoading(false);
  };

  const handleDeleteSpeechMessage = async (index) => {
    showConfirm('Delete this speech bubble message?', async () => {
      setLoading(true);
      try {
        const updatedMessages = speechBubbleMessages.filter((_, i) => i !== index);
        const messagesRef = ref(database, 'config/voiceMemoMessages');
        await set(messagesRef, updatedMessages);
        setSpeechBubbleMessages(updatedMessages);
        showMessage('Message deleted!');
      } catch (error) {
        console.error('Error deleting message:', error);
        showMessage('Error deleting message', 'error');
      }
      setLoading(false);
    });
  };

  const handleUpdatePhotoCaption = async (photoId, newCaption) => {
    try {
      const photoRef = ref(database, `photos/frame/${photoId}/caption`);
      await set(photoRef, newCaption);
      setFramePhotos(framePhotos.map(p =>
        p.id === photoId ? { ...p, caption: newCaption } : p
      ));
    } catch (error) {
      console.error('Error updating caption:', error);
      showMessage('Error updating caption', 'error');
    }
  };

  // Delete connections puzzle
  const handleDeleteConnectionsPuzzle = async (puzzleId) => {
    showConfirm('Are you sure you want to delete this connections puzzle?', async () => {
      setLoading(true);
      try {
        const puzzleRef = ref(database, `puzzles/connections/${puzzleId}`);
        await remove(puzzleRef);
        setConnectionsPuzzles(connectionsPuzzles.filter(p => p.id !== puzzleId));
        showMessage('Connections puzzle deleted successfully!');
      } catch (error) {
        console.error('Error deleting connections puzzle:', error);
        showMessage('Error deleting puzzle', 'error');
      }
      setLoading(false);
    });
  };

  // Delete a pinned sticky note
  const handleDeletePinnedNote = async (noteId) => {
    showConfirm('Are you sure you want to delete this pinned note from the wall?', async () => {
      setLoading(true);
      try {
        const noteRef = ref(database, `wall/pinnedNotes/${noteId}`);
        await remove(noteRef);
        setPinnedNotes(pinnedNotes.filter((note) => note.id !== noteId));
        showMessage('Pinned note deleted successfully!');
      } catch (error) {
        console.error('Error deleting pinned note:', error);
        showMessage('Error deleting pinned note', 'error');
      }
      setLoading(false);
    });
  };

  // Clear all pinned sticky notes
  const handleClearAllPinnedNotes = async () => {
    showConfirm('Are you sure you want to delete ALL pinned notes from the wall? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const pinnedRef = ref(database, 'wall/pinnedNotes');
        await remove(pinnedRef);
        setPinnedNotes([]);
        showMessage('All pinned notes cleared successfully!');
      } catch (error) {
        console.error('Error clearing pinned notes:', error);
        showMessage('Error clearing pinned notes', 'error');
      }
      setLoading(false);
    });
  };

  // Unpin a note and return it to the desk
  const handleUnpinNote = async (noteId, noteData) => {
    showConfirm('Return this note to the desk?', async () => {
      setLoading(true);
      try {
        // Create a new sticky note on the desk
        const notesRef = ref(database, 'desk/stickyNotes');
        const newNoteRef = push(notesRef);

        const deskNote = {
          text: noteData.text || '',
          drawing: noteData.drawing || null,
          position: { x: 100 + Math.random() * 200, y: 100 + Math.random() * 150 },
          color: noteData.color || 'var(--color-watercolor-yellow)',
          createdBy: noteData.createdBy || 'Unknown',
          createdByEmail: noteData.createdByEmail || 'unknown',
          createdAt: new Date().toISOString(),
        };

        await set(newNoteRef, deskNote);

        // Remove from wall
        const pinnedNoteRef = ref(database, `wall/pinnedNotes/${noteId}`);
        await remove(pinnedNoteRef);

        setPinnedNotes(pinnedNotes.filter((note) => note.id !== noteId));
        showMessage('Note returned to desk!');
      } catch (error) {
        console.error('Error unpinning note:', error);
        showMessage('Error unpinning note', 'error');
      }
      setLoading(false);
    });
  };

  // Clear wordle progress for a specific date
  const handleClearWordleProgress = async (date) => {
    showConfirm(`Are you sure you want to clear all progress for the Wordle on ${date}? Both users will be able to play it again.`, async () => {
      setLoading(true);
      try {
        const progressRef = ref(database, `puzzles/wordle/${date}/progress`);
        await remove(progressRef);
        // Reload wordle progress
        await loadConfigurations();
        showMessage('Wordle progress cleared successfully!');
      } catch (error) {
        console.error('Error clearing wordle progress:', error);
        showMessage('Error clearing wordle progress', 'error');
      }
      setLoading(false);
    });
  };

  // Clear all wordle progress (all dates)
  const handleClearAllWordleProgress = async () => {
    showConfirm('Are you sure you want to clear ALL Wordle progress for ALL dates? This cannot be undone!', async () => {
      setLoading(true);
      try {
        const wordleRef = ref(database, 'puzzles/wordle');
        await remove(wordleRef);
        setWordleProgress([]);
        showMessage('All Wordle progress cleared successfully!');
      } catch (error) {
        console.error('Error clearing all wordle progress:', error);
        showMessage('Error clearing wordle progress', 'error');
      }
      setLoading(false);
    });
  };

  // Clear all connections puzzles progress (reset for all users)
  const handleResetPuzzleProgress = async (puzzleId) => {
    showConfirm('Are you sure you want to reset all progress for this puzzle? Both users will be able to play it again.', async () => {
      setLoading(true);
      try {
        const progressRef = ref(database, `puzzles/connections/${puzzleId}/progress`);
        await remove(progressRef);
        // Reload puzzles to update the UI
        await loadConfigurations();
        showMessage('Puzzle progress reset successfully!');
      } catch (error) {
        console.error('Error resetting puzzle progress:', error);
        showMessage('Error resetting progress', 'error');
      }
      setLoading(false);
    });
  };

  // Delete a user from the database (NOT from Firebase Auth - just the DB record)
  const handleDeleteUser = async (uid, userName) => {
    showConfirm(`Are you sure you want to delete the database record for "${userName}" (UID: ${uid})? This will NOT delete the Firebase Auth account, just the database entry.`, async () => {
      setLoading(true);
      try {
        const userRef = ref(database, `users/${uid}`);
        await remove(userRef);
        setAllUsers(allUsers.filter((user) => user.uid !== uid));
        showMessage(`User "${userName}" database record deleted successfully!`);
      } catch (error) {
        console.error('Error deleting user:', error);
        showMessage('Error deleting user', 'error');
      }
      setLoading(false);
    });
  };

  // Clear all user database entries (dangerous!)
  const handleClearAllUsers = async () => {
    showConfirm('Are you sure you want to delete ALL user database records? This is VERY dangerous and cannot be undone! Users will need to log in again to recreate their profiles.', async () => {
      setLoading(true);
      try {
        const usersRef = ref(database, 'users');
        await remove(usersRef);
        setAllUsers([]);
        showMessage('All user database records cleared successfully!');
      } catch (error) {
        console.error('Error clearing users:', error);
        showMessage('Error clearing users', 'error');
      }
      setLoading(false);
    });
  };

  // Password update handler
  const handleUpdateIntimacyPassword = async () => {
    if (!intimacyPassword.trim()) {
      showMessage('Password cannot be empty', 'error');
      return;
    }
    setLoading(true);
    try {
      const passwordRef = ref(database, 'settings/intimacyPassword');
      await set(passwordRef, intimacyPassword.trim());
      showMessage('Intimacy password updated successfully!');
    } catch (error) {
      console.error('Error updating intimacy password:', error);
      showMessage('Error updating password', 'error');
    }
    setLoading(false);
  };

  // If not admin, don't render anything (will redirect)
  if (!userProfile || userProfile.name?.toLowerCase() !== 'admin') {
    return null;
  }

  return (
    <div className="admin-page">
      {/* Admin Header */}
      <motion.div
        className="admin-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1 className="admin-title">Admin Panel</h1>
        <p className="admin-subtitle">Manage app settings and configurations</p>
        <p className="admin-user">Logged in as: {userProfile?.name || 'Admin'}</p>
      </motion.div>

      {/* Status Message */}
      {message && (
        <motion.div
          className={`admin-message ${message.type}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
        >
          {message.text}
        </motion.div>
      )}

      {/* Tab Navigation */}
      <motion.div
        className="admin-tabs-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        {/* Content Group */}
        <div className="tab-group">
          <span className="tab-group-label">Content</span>
          <div className="tab-group-buttons">
            <button
              className={`admin-tab ${activeTab === 'journal' ? 'active' : ''}`}
              onClick={() => setActiveTab('journal')}
            >
              📖 Journal
            </button>
            <button
              className={`admin-tab ${activeTab === 'poetry' ? 'active' : ''}`}
              onClick={() => setActiveTab('poetry')}
            >
              📜 Poetry
            </button>
            <button
              className={`admin-tab ${activeTab === 'letters' ? 'active' : ''}`}
              onClick={() => setActiveTab('letters')}
            >
              ✉️ Letters
            </button>
            <button
              className={`admin-tab ${activeTab === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              📝 Sticky Notes
            </button>
            <button
              className={`admin-tab ${activeTab === 'pinned' ? 'active' : ''}`}
              onClick={() => setActiveTab('pinned')}
            >
              📌 Pinned
            </button>
          </div>
        </div>

        {/* Media Group */}
        <div className="tab-group">
          <span className="tab-group-label">Media</span>
          <div className="tab-group-buttons">
            <button
              className={`admin-tab ${activeTab === 'photos' ? 'active' : ''}`}
              onClick={() => setActiveTab('photos')}
            >
              📷 Photos
            </button>
            <button
              className={`admin-tab ${activeTab === 'voicememos' ? 'active' : ''}`}
              onClick={() => setActiveTab('voicememos')}
            >
              🎙️ Voice Memos
            </button>
          </div>
        </div>

        {/* Games Group */}
        <div className="tab-group">
          <span className="tab-group-label">Games</span>
          <div className="tab-group-buttons">
            <button
              className={`admin-tab ${activeTab === 'words' ? 'active' : ''}`}
              onClick={() => setActiveTab('words')}
            >
              🔤 Word Bucket
            </button>
            <button
              className={`admin-tab ${activeTab === 'connections' ? 'active' : ''}`}
              onClick={() => setActiveTab('connections')}
            >
              🧩 Connections
            </button>
            <button
              className={`admin-tab ${activeTab === 'wordle' ? 'active' : ''}`}
              onClick={() => setActiveTab('wordle')}
            >
              🟩 Wordle
            </button>
          </div>
        </div>

        {/* Settings Group */}
        <div className="tab-group">
          <span className="tab-group-label">Settings</span>
          <div className="tab-group-buttons">
            <button
              className={`admin-tab ${activeTab === 'dates' ? 'active' : ''}`}
              onClick={() => setActiveTab('dates')}
            >
              📅 Dates
            </button>
            <button
              className={`admin-tab ${activeTab === 'passwords' ? 'active' : ''}`}
              onClick={() => setActiveTab('passwords')}
            >
              🔐 Passwords
            </button>
            <button
              className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              👥 Users
            </button>
            <button
              className={`admin-tab ${activeTab === 'layout' ? 'active' : ''}`}
              onClick={() => setActiveTab('layout')}
            >
              🖼️ Layout
            </button>
            <button
              className={`admin-tab ${activeTab === 'cursor' ? 'active' : ''}`}
              onClick={() => setActiveTab('cursor')}
            >
              🖊️ Cursor
            </button>
          </div>
        </div>

        {/* Private Group */}
        <div className="tab-group">
          <span className="tab-group-label">Private</span>
          <div className="tab-group-buttons">
            <button
              className={`admin-tab intimacy-tab ${activeTab === 'intimacy' ? 'active' : ''}`}
              onClick={() => setActiveTab('intimacy')}
            >
              🔒 Intimacy
            </button>
          </div>
        </div>
      </motion.div>

      {/* Admin Content */}
      <motion.div
        className="admin-content"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        {activeTab === 'dates' && (
          <div className="admin-section">
            <h2>Date Configurations</h2>

            <div className="config-group">
              <label htmlFor="nextVisitDate">Next Visit Date:</label>
              <input
                type="date"
                id="nextVisitDate"
                value={nextVisitDate}
                onChange={(e) => setNextVisitDate(e.target.value)}
                className="date-input"
              />
              <button
                onClick={handleSaveNextVisitDate}
                disabled={loading || !nextVisitDate}
                className="save-button"
              >
                {loading ? 'Saving...' : 'Save Next Visit Date'}
              </button>
            </div>

            <div className="config-group">
              <label htmlFor="relationshipStartDate">Relationship Start Date:</label>
              <input
                type="date"
                id="relationshipStartDate"
                value={relationshipStartDate}
                onChange={(e) => setRelationshipStartDate(e.target.value)}
                className="date-input"
              />
              <button
                onClick={handleSaveRelationshipStartDate}
                disabled={loading || !relationshipStartDate}
                className="save-button"
              >
                {loading ? 'Saving...' : 'Save Start Date'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="admin-section">
            <h2>Sticky Notes Management</h2>

            <div className="notes-actions">
              <button
                onClick={handleClearEmptyStickyNotes}
                disabled={loading || stickyNotes.filter(n => !n.text || n.text.trim() === '').length === 0}
                className="warning-button"
              >
                🗑️ Clear Empty Notes ({stickyNotes.filter(n => !n.text || n.text.trim() === '').length})
              </button>
              <button
                onClick={handleClearAllStickyNotes}
                disabled={loading || stickyNotes.length === 0}
                className="danger-button"
              >
                ⚠️ Clear All Sticky Notes
              </button>
              <p className="notes-count">Total Notes: {stickyNotes.length}</p>
            </div>

            <div className="notes-list">
              {stickyNotes.length === 0 ? (
                <p className="no-notes">No sticky notes on the desk</p>
              ) : (
                stickyNotes.map((note) => (
                  <div key={note.id} className={`note-item ${!note.text || note.text.trim() === '' ? 'empty-note' : ''}`}>
                    <div className="note-info">
                      <p className="note-author">By: {note.createdBy}</p>
                      <p className="note-date">
                        Created: {new Date(note.createdAt).toLocaleString()}
                      </p>
                      <p className="note-text">
                        {note.text || '<empty note>'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteStickyNote(note.id)}
                      disabled={loading}
                      className="delete-note-button"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'journal' && (
          <div className="admin-section">
            <h2>Journal Entries Management</h2>

            <div className="content-list">
              {journalEntries.length === 0 ? (
                <p className="no-content">No journal entries</p>
              ) : (
                journalEntries.map((entry) => (
                  <div key={entry.id} className="content-item">
                    <div className="content-info">
                      <p className="content-title">{entry.title || 'Untitled'}</p>
                      <p className="content-author">By: {entry.createdBy}</p>
                      <p className="content-date">
                        {new Date(entry.createdAt).toLocaleString()}
                      </p>
                      <p className="content-preview">
                        {entry.content?.substring(0, 100)}...
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteJournalEntry(entry.id)}
                      disabled={loading}
                      className="delete-content-button"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'poetry' && (
          <div className="admin-section">
            <h2>Poetry Management</h2>

            <div className="content-list">
              {poems.length === 0 ? (
                <p className="no-content">No poems</p>
              ) : (
                poems.map((poem) => (
                  <div key={poem.id} className="content-item">
                    <div className="content-info">
                      <p className="content-title">{poem.title || 'Untitled'}</p>
                      <p className="content-author">By: {poem.createdBy}</p>
                      <p className="content-date">
                        {new Date(poem.createdAt).toLocaleString()}
                      </p>
                      <p className="content-preview">
                        {poem.content?.substring(0, 100)}...
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeletePoem(poem.id)}
                      disabled={loading}
                      className="delete-content-button"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'words' && (
          <div className="admin-section">
            <h2>Wordle Word Bucket</h2>
            <p className="section-description">
              Manage 5-letter words used for daily Wordle puzzles. Words are automatically rotated each day (EST timezone).
            </p>

            <div className="word-input-section">
              <input
                type="text"
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddWord()}
                placeholder="Enter a 5-letter word..."
                className="word-input"
                maxLength={5}
              />
              <button
                onClick={handleAddWord}
                disabled={loading || !newWord.trim()}
                className="add-word-button"
              >
                ➕ Add Word
              </button>
              <button
                onClick={handleClearWordBucket}
                disabled={loading || wordBucket.length === 0}
                className="danger-button"
              >
                ⚠️ Clear All Words
              </button>
            </div>

            <p className="word-count">Total Words: {wordBucket.length}</p>

            <div className="word-bucket-grid">
              {wordBucket.length === 0 ? (
                <p className="no-words">No words in the bucket yet. Start adding some!</p>
              ) : (
                wordBucket.map((wordItem) => (
                  <motion.div
                    key={wordItem.id}
                    className="word-item"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <span className="word-text">{wordItem.word}</span>
                    <button
                      onClick={() => handleDeleteWord(wordItem.id)}
                      disabled={loading}
                      className="delete-word-button"
                      title="Delete word"
                    >
                      ✕
                    </button>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'letters' && (
          <div className="admin-section">
            <h2>Letters Management</h2>

            <div className="content-list">
              {letters.length === 0 ? (
                <p className="no-content">No letters</p>
              ) : (
                letters.map((letter) => (
                  <div key={letter.id} className="content-item">
                    <div className="content-info">
                      <p className="content-title">Letter from {letter.author || letter.createdBy || 'Unknown'}</p>
                      <p className="content-author">Email: {letter.authorEmail || 'Unknown'}</p>
                      <p className="content-date">
                        {new Date(letter.createdAt).toLocaleString()}
                      </p>
                      <p className="content-seal-info">
                        Seal: {letter.sealMethod || 'None'}
                        {letter.sealedMoonPhase && ` - ${letter.sealedMoonPhase}`}
                        {letter.delayDays && ` - ${letter.delayDays} days`}
                      </p>
                      <p className="content-preview">
                        {letter.content?.substring(0, 100)}...
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteLetter(letter.id)}
                      disabled={loading}
                      className="delete-content-button"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="admin-section">
            <h2>Picture Frame Photos</h2>
            <p className="section-description">
              Upload photos to rotate in the picture frame on the home wall. Photos will cycle automatically.
            </p>

            <div className="photo-upload-section">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="photo-file-input"
                id="photo-upload"
                disabled={photoUploading}
              />
              <label
                htmlFor="photo-upload"
                className={`photo-upload-label ${photoUploading ? 'uploading' : ''}`}
              >
                {photoUploading ? '⏳ Uploading... Please wait' : '📷 Choose Photos'}
              </label>
              {photoUploading && (
                <p className="upload-status">Processing images... Check console for details.</p>
              )}
              <p className="photo-upload-hint">Max 5MB per image. Supports JPG, PNG, GIF.</p>
            </div>

            <p className="photo-count">Total Photos: {framePhotos.length}</p>

            <div className="photo-grid">
              {framePhotos.length === 0 ? (
                <p className="no-photos">No photos uploaded yet. Add some memories!</p>
              ) : (
                framePhotos.map((photo) => (
                  <motion.div
                    key={photo.id}
                    className="photo-item"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <img
                      src={photo.url}
                      alt={photo.caption || 'Frame photo'}
                      className="photo-thumbnail"
                    />
                    <div className="photo-details">
                      <input
                        type="text"
                        value={photo.caption || ''}
                        onChange={(e) => handleUpdatePhotoCaption(photo.id, e.target.value)}
                        placeholder="Add caption..."
                        className="photo-caption-input"
                      />
                      <p className="photo-date">
                        {new Date(photo.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      disabled={loading}
                      className="delete-photo-button"
                      title="Delete photo"
                    >
                      🗑️
                    </button>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'layout' && (
          <div className="admin-section">
            <WallLayoutEditor />
          </div>
        )}

        {activeTab === 'passwords' && (
          <div className="admin-section">
            <h2>Password Management</h2>
            <p className="section-description">
              Manage passwords for the Intimacy page and user accounts.
            </p>

            {/* Intimacy Page Password */}
            <div className="password-section">
              <h3>Intimacy Page Password</h3>
              <p className="password-hint">
                This password is required to access the Intimacy page.
              </p>
              <div className="password-input-group">
                <input
                  type="text"
                  value={intimacyPassword}
                  onChange={(e) => setIntimacyPassword(e.target.value)}
                  placeholder="Enter intimacy password"
                  className="password-input"
                />
                <button
                  onClick={handleUpdateIntimacyPassword}
                  disabled={loading}
                  className="save-password-button"
                >
                  {loading ? 'Saving...' : 'Save Password'}
                </button>
              </div>
            </div>

            {/* Account Passwords Notice */}
            <div className="password-section account-passwords">
              <h3>Account Passwords</h3>
              <p className="password-hint">
                Account passwords (Henry, Omi, Admin) are managed through Firebase Authentication.
                To change these passwords, please use one of the following methods:
              </p>
              <ul className="password-instructions">
                <li>
                  <strong>Firebase Console:</strong> Go to Authentication → Users → Select user → Reset password
                </li>
                <li>
                  <strong>Password Reset Email:</strong> Users can request a password reset from the login page
                </li>
              </ul>
              <div className="account-list">
                <div className="account-item">
                  <span className="account-name">Henry</span>
                  <span className="account-email">henry@ldr-webapp.com</span>
                </div>
                <div className="account-item">
                  <span className="account-name">Omi</span>
                  <span className="account-email">omi@ldr-webapp.com</span>
                </div>
                <div className="account-item">
                  <span className="account-name">Admin</span>
                  <span className="account-email">admin@ldr-webapp.com</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'connections' && (
          <div className="admin-section">
            <h2>Connections Puzzles Management</h2>
            <p className="section-description">
              Manage custom Connections puzzles created by users. You can delete puzzles or reset their progress.
            </p>

            <p className="puzzle-count">Total Puzzles: {connectionsPuzzles.length}</p>

            <div className="connections-admin-list">
              {connectionsPuzzles.length === 0 ? (
                <p className="no-content">No connections puzzles have been created yet.</p>
              ) : (
                connectionsPuzzles.map((puzzle) => (
                  <div key={puzzle.id} className="connections-puzzle-item">
                    <div className="puzzle-info">
                      <h4 className="puzzle-title">{puzzle.title || 'Untitled Puzzle'}</h4>
                      <p className="puzzle-creator">Created by: {puzzle.creatorName || 'Unknown'}</p>
                      <p className="puzzle-date">
                        {puzzle.createdAt ? new Date(puzzle.createdAt).toLocaleString() : 'Unknown date'}
                      </p>
                      {puzzle.completionMessage && (
                        <p className="puzzle-completion-msg">Completion message: "{puzzle.completionMessage}"</p>
                      )}
                      <div className="puzzle-groups-preview">
                        <strong>Groups:</strong>
                        {puzzle.groups && puzzle.groups.map((group, idx) => (
                          <span key={idx} className="group-preview">
                            {group.category}: {group.words ? group.words.join(', ') : 'No words'}
                          </span>
                        ))}
                      </div>
                      {puzzle.progress && (
                        <div className="puzzle-progress-info">
                          <strong>Progress:</strong>
                          {Object.entries(puzzle.progress).map(([uid, prog]) => (
                            <span key={uid} className={`progress-badge ${prog.failed ? 'failed' : prog.completed ? 'completed' : 'in-progress'}`}>
                              {prog.completed ? (prog.failed ? `Failed (${prog.solvedCount || 0}/4)` : `Completed (${prog.mistakes || 0} mistakes)`) : 'In progress'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="puzzle-actions">
                      <button
                        onClick={() => handleResetPuzzleProgress(puzzle.id)}
                        disabled={loading}
                        className="reset-puzzle-button"
                        title="Reset progress for all users"
                      >
                        🔄 Reset Progress
                      </button>
                      <button
                        onClick={() => handleDeleteConnectionsPuzzle(puzzle.id)}
                        disabled={loading}
                        className="delete-puzzle-button"
                        title="Delete puzzle"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'pinned' && (
          <div className="admin-section">
            <h2>Pinned Sticky Notes Management</h2>
            <p className="section-description">
              Manage sticky notes that have been pinned to the wall. These notes are visible to both users on the home page.
            </p>

            <div className="notes-actions">
              <button
                onClick={handleClearAllPinnedNotes}
                disabled={loading || pinnedNotes.length === 0}
                className="danger-button"
              >
                Clear All Pinned Notes
              </button>
              <p className="notes-count">Total Pinned Notes: {pinnedNotes.length}</p>
            </div>

            <div className="notes-list">
              {pinnedNotes.length === 0 ? (
                <p className="no-notes">No pinned notes on the wall</p>
              ) : (
                pinnedNotes.map((note) => (
                  <div key={note.id} className="note-item pinned-note-item">
                    <div className="note-info">
                      <p className="note-author">By: {note.createdBy || 'Unknown'}</p>
                      <p className="note-date">
                        Pinned: {note.createdAt ? new Date(note.createdAt).toLocaleString() : 'Unknown'}
                      </p>
                      <p className="note-text">
                        {note.text || '<empty note>'}
                      </p>
                      <p className="note-position">
                        Position: ({Math.round(note.x || 0)}%, {Math.round(note.y || 0)}%)
                      </p>
                    </div>
                    <div className="note-actions">
                      <button
                        onClick={() => handleUnpinNote(note.id, note)}
                        disabled={loading}
                        className="action-button"
                      >
                        Return to Desk
                      </button>
                      <button
                        onClick={() => handleDeletePinnedNote(note.id)}
                        disabled={loading}
                        className="delete-note-button"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'wordle' && (
          <div className="admin-section">
            <h2>Wordle Progress Management</h2>
            <p className="section-description">
              View and manage Wordle game progress. You can reset progress for specific dates or clear all progress.
            </p>

            <div className="wordle-actions">
              <button
                onClick={handleClearAllWordleProgress}
                disabled={loading || wordleProgress.length === 0}
                className="danger-button"
              >
                ⚠️ Clear All Wordle Progress
              </button>
              <p className="wordle-count">Total Wordle Days: {wordleProgress.length}</p>
            </div>

            <div className="wordle-list">
              {wordleProgress.length === 0 ? (
                <p className="no-content">No Wordle games have been played yet</p>
              ) : (
                wordleProgress.map((wordle) => (
                  <div key={wordle.date} className="wordle-item">
                    <div className="wordle-info">
                      <h4 className="wordle-date">{wordle.date}</h4>
                      <p className="wordle-word">Word: <strong>{wordle.word || 'Not set'}</strong></p>
                      {wordle.progress && Object.keys(wordle.progress).length > 0 ? (
                        <div className="wordle-progress-details">
                          <strong>Players:</strong>
                          {Object.entries(wordle.progress).map(([uid, prog]) => (
                            <span key={uid} className={`progress-badge ${prog.won ? 'completed' : prog.completed ? 'failed' : 'in-progress'}`}>
                              {prog.completed
                                ? (prog.won ? `Won (${prog.guesses?.length || 0}/6)` : 'Lost')
                                : `In progress (${prog.guesses?.length || 0}/6)`}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="no-progress">No one has played this Wordle yet</p>
                      )}
                    </div>
                    <div className="wordle-actions-buttons">
                      <button
                        onClick={() => handleClearWordleProgress(wordle.date)}
                        disabled={loading || !wordle.progress || Object.keys(wordle.progress).length === 0}
                        className="reset-puzzle-button"
                        title="Reset progress for this date"
                      >
                        🔄 Reset Progress
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-section">
            <h2>User Database Management</h2>
            <p className="section-description">
              View and manage user records in the database. Use this to clean up old user entries after creating new accounts.
              <br /><strong>Note:</strong> This only deletes database records, not Firebase Auth accounts.
            </p>

            <div className="users-actions">
              <button
                onClick={handleClearAllUsers}
                disabled={loading || allUsers.length === 0}
                className="danger-button"
              >
                ⚠️ Clear All User Records
              </button>
              <button
                onClick={loadConfigurations}
                disabled={loading}
                className="save-button"
              >
                🔄 Refresh
              </button>
              <p className="users-count">Total Users in Database: {allUsers.length}</p>
            </div>

            {currentUser && (
              <div className="current-user-info">
                <strong>Currently logged in UID:</strong> <code>{currentUser.uid}</code>
                <p className="current-user-hint">Keep this UID and delete any others with the same name!</p>
              </div>
            )}

            <div className="users-list">
              {allUsers.length === 0 ? (
                <p className="no-content">No users found in database</p>
              ) : (
                allUsers.map((user) => {
                  const isCurrentUser = currentUser && user.uid === currentUser.uid;
                  return (
                    <div key={user.uid} className={`user-item ${isCurrentUser ? 'current-user' : ''} ${user.hasActivity ? 'has-activity' : 'no-activity'}`}>
                      <div className="user-info">
                        <h4 className="user-name">
                          {user.name || 'Unknown Name'}
                          {isCurrentUser && <span className="current-badge">CURRENT LOGIN</span>}
                          {user.hasActivity && <span className="active-badge">HAS OLD DATA</span>}
                          {!user.hasActivity && !isCurrentUser && <span className="inactive-badge">NO DATA</span>}
                        </h4>
                        <p className="user-uid"><strong>UID:</strong> <code>{user.uid}</code></p>
                        <p className="user-email"><strong>Email:</strong> {user.email || 'Not set'}</p>
                        <p className="user-role"><strong>Role:</strong> {user.role || 'user'}</p>
                        {user.lastTimezone && (
                          <p className="user-timezone"><strong>Last Timezone:</strong> {user.lastTimezone}</p>
                        )}
                        {user.activityDetails && (
                          <div className="user-activity">
                            <strong>Associated Data:</strong>
                            <span className={user.activityDetails.journal ? 'has' : 'none'}>Journal {user.activityDetails.journal ? '✓' : '✗'}</span>
                            <span className={user.activityDetails.poems ? 'has' : 'none'}>Poems {user.activityDetails.poems ? '✓' : '✗'}</span>
                            <span className={user.activityDetails.stickyNotes ? 'has' : 'none'}>Notes {user.activityDetails.stickyNotes ? '✓' : '✗'}</span>
                            <span className={user.activityDetails.wordle ? 'has' : 'none'}>Wordle {user.activityDetails.wordle ? '✓' : '✗'}</span>
                          </div>
                        )}
                        {isCurrentUser && (
                          <p className="keep-this-warning">⚠️ This is your current account - DO NOT DELETE</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteUser(user.uid, user.name)}
                        disabled={loading || isCurrentUser}
                        className="delete-user-button"
                        title={isCurrentUser ? "Cannot delete currently logged in account" : "Delete this user record"}
                      >
                        🗑️ Delete Record
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="users-help">
              <h3>How to fix "Dear Omi and Omi" or similar issues:</h3>
              <ol>
                <li><strong>Identify the old accounts:</strong> Look for entries marked "HAS OLD DATA" - these are from your OLD Firebase Auth accounts</li>
                <li><strong>Identify current accounts:</strong> The entry marked "CURRENT LOGIN" is your new account (do NOT delete it)</li>
                <li><strong>Delete old records:</strong> Delete the old user records that are NOT marked "CURRENT LOGIN"</li>
                <li><strong>Repeat for each user:</strong> Log in as Henry, then as Omi, delete their old records too</li>
              </ol>

              <h3>Is my data safe?</h3>
              <p><strong>YES!</strong> Your content (poems, journal entries, sticky notes, letters) is stored by NAME, not by UID. Deleting old user records will NOT delete your content.</p>
              <ul>
                <li>✅ Journal entries - stored by name, safe</li>
                <li>✅ Poems - stored by name, safe</li>
                <li>✅ Sticky notes - stored by name, safe</li>
                <li>✅ Letters - stored by name, safe</li>
                <li>✅ Photos - not tied to users, safe</li>
                <li>✅ Connections puzzles - puzzles safe, displayed by name</li>
                <li>⚠️ Connections progress - tied to old UID (reset from Connections tab)</li>
                <li>⚠️ Wordle progress - tied to old UID (reset from Wordle tab)</li>
              </ul>

              <p><strong>The issue:</strong> When you created new accounts, they got new UIDs. The app sees multiple "Omi" or "Henry" records and gets confused about which partner to show.</p>
            </div>
          </div>
        )}

        {activeTab === 'cursor' && (
          <div className="admin-section">
            <h2>Cursor Settings</h2>
            <p className="section-description">
              Configure the custom cursor for all users. The cursor only appears after login - the login page always uses the default cursor.
            </p>

            <div className="config-group">
              <label>Cursor Type:</label>
              <div className="cursor-type-options">
                <button
                  className={`cursor-option-btn ${cursorSettings.globalCursorType === 'default' ? 'active' : ''}`}
                  onClick={() => setCursorSettings(prev => ({ ...prev, globalCursorType: 'default' }))}
                >
                  <span className="cursor-icon">🖱️</span>
                  <span className="cursor-label">Default</span>
                  <span className="cursor-desc">Standard system cursor</span>
                </button>
                <button
                  className={`cursor-option-btn ${cursorSettings.globalCursorType === 'pen' ? 'active' : ''}`}
                  onClick={() => setCursorSettings(prev => ({ ...prev, globalCursorType: 'pen' }))}
                >
                  <span className="cursor-icon">🖊️</span>
                  <span className="cursor-label">Fountain Pen</span>
                  <span className="cursor-desc">Elegant pen with ink trail</span>
                </button>
                <button
                  className={`cursor-option-btn ${cursorSettings.globalCursorType === 'leaf' ? 'active' : ''}`}
                  onClick={() => setCursorSettings(prev => ({ ...prev, globalCursorType: 'leaf' }))}
                >
                  <span className="cursor-icon">🍂</span>
                  <span className="cursor-label">Fall Leaf</span>
                  <span className="cursor-desc">Orange leaf with falling leaves trail</span>
                </button>
                <button
                  className={`cursor-option-btn ${cursorSettings.globalCursorType === 'flower' ? 'active' : ''}`}
                  onClick={() => setCursorSettings(prev => ({ ...prev, globalCursorType: 'flower' }))}
                >
                  <span className="cursor-icon">🌸</span>
                  <span className="cursor-label">Spring Flower</span>
                  <span className="cursor-desc">Pink flower with bee friends</span>
                </button>
              </div>
            </div>

            {(cursorSettings.globalCursorType === 'pen' || cursorSettings.globalCursorType === 'leaf' || cursorSettings.globalCursorType === 'flower') && (
              <div className="config-group">
                <label>
                  <input
                    type="checkbox"
                    checked={cursorSettings.trailEnabled !== false}
                    onChange={(e) => setCursorSettings(prev => ({ ...prev, trailEnabled: e.target.checked }))}
                  />
                  {' '}Enable {cursorSettings.globalCursorType === 'pen' ? 'Ink' : cursorSettings.globalCursorType === 'leaf' ? 'Falling Leaves' : 'Bee'} Trail Effect
                </label>
                <p className="config-hint">
                  {cursorSettings.globalCursorType === 'pen'
                    ? 'When enabled, the pen leaves a subtle ink trail as you move'
                    : cursorSettings.globalCursorType === 'leaf'
                    ? 'When enabled, falling leaves trail behind as you move'
                    : 'When enabled, bees follow your flower and land when you stop'}
                </p>
              </div>
            )}

            <button
              onClick={handleSaveCursorSettings}
              disabled={loading}
              className="save-button"
            >
              {loading ? 'Saving...' : '💾 Save Cursor Settings'}
            </button>
          </div>
        )}

        {activeTab === 'voicememos' && (
          <div className="admin-section">
            <h2>Voice Memos Management</h2>

            {/* Speech Bubble Messages */}
            <div className="config-group">
              <h3 className="subsection-title">Speech Bubble Messages</h3>
              <p className="section-description">
                These messages appear randomly on the Voice Memos page.
              </p>

              <div className="speech-message-input-row">
                <input
                  type="text"
                  value={newSpeechMessage}
                  onChange={(e) => setNewSpeechMessage(e.target.value)}
                  placeholder="Enter a new message..."
                  className="text-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSpeechMessage()}
                />
                <button
                  onClick={handleAddSpeechMessage}
                  disabled={loading || !newSpeechMessage.trim()}
                  className="add-button"
                >
                  + Add
                </button>
              </div>

              {speechBubbleMessages.length === 0 ? (
                <p className="empty-message">No messages yet. Add some fun messages!</p>
              ) : (
                <div className="speech-messages-list">
                  {speechBubbleMessages.map((msg, index) => (
                    <div key={index} className="speech-message-item">
                      <span className="speech-message-text">"{msg}"</span>
                      <button
                        className="delete-small-btn"
                        onClick={() => handleDeleteSpeechMessage(index)}
                        disabled={loading}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Voice Recordings */}
            <div className="config-group">
              <h3 className="subsection-title">Saved Recordings</h3>
              <p className="section-description">
                Manage voice recordings. You can listen to and delete memos here.
              </p>

              {voiceMemos.length === 0 ? (
                <p className="empty-message">No voice memos recorded yet.</p>
              ) : (
                <div className="voice-memos-admin-list">
                  {voiceMemos.map((memo) => (
                    <div key={memo.id} className="voice-memo-admin-item">
                      <div className="memo-details">
                        <span className="memo-title-admin">{memo.title}</span>
                        <span className="memo-info-admin">
                          {formatMemoTime(memo.duration)} • {memo.recordedBy} • {new Date(memo.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="memo-admin-actions">
                        <button
                          className={`memo-action-btn play ${playingMemoId === memo.id ? 'playing' : ''}`}
                          onClick={() => handlePlayMemo(memo)}
                        >
                          {playingMemoId === memo.id ? '⏹ Stop' : '▶ Play'}
                        </button>
                        <button
                          className="memo-action-btn delete"
                          onClick={() => handleDeleteMemo(memo.id, memo.title)}
                          disabled={loading}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'intimacy' && (
          <div className="admin-section intimacy-admin-section">
            <h2>Intimacy Content Management</h2>

            {!intimacyUnlocked ? (
              <div className="intimacy-unlock-section">
                <p className="section-description">
                  This section is password protected. Enter the intimacy password to manage intimate content.
                </p>
                <div className="intimacy-password-form">
                  <input
                    type="password"
                    value={intimacyPasswordInput}
                    onChange={(e) => {
                      setIntimacyPasswordInput(e.target.value);
                      setIntimacyPasswordError('');
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleUnlockIntimacy()}
                    placeholder="Enter intimacy password..."
                    className="intimacy-password-input"
                  />
                  <button
                    onClick={handleUnlockIntimacy}
                    disabled={loading || !intimacyPasswordInput}
                    className="unlock-button"
                  >
                    Unlock
                  </button>
                </div>
                {intimacyPasswordError && (
                  <p className="intimacy-error">{intimacyPasswordError}</p>
                )}
              </div>
            ) : (
              <div className="intimacy-content-sections">
                <p className="section-description">
                  Manage all intimacy content including Eros poems/whispers and Freaky mode elements.
                </p>

                {/* Sub-navigation for intimacy sections */}
                <div className="intimacy-sub-nav">
                  <button
                    className="intimacy-sub-tab active"
                    data-section="eros-poems"
                    onClick={(e) => {
                      document.querySelectorAll('.intimacy-sub-tab').forEach(t => t.classList.remove('active'));
                      e.target.classList.add('active');
                      document.querySelectorAll('.intimacy-sub-section').forEach(s => s.style.display = 'none');
                      document.getElementById('eros-poems-section').style.display = 'block';
                    }}
                  >
                    Eros Poems ({intimacyPoems.length})
                  </button>
                  <button
                    className="intimacy-sub-tab"
                    data-section="eros-whispers"
                    onClick={(e) => {
                      document.querySelectorAll('.intimacy-sub-tab').forEach(t => t.classList.remove('active'));
                      e.target.classList.add('active');
                      document.querySelectorAll('.intimacy-sub-section').forEach(s => s.style.display = 'none');
                      document.getElementById('eros-whispers-section').style.display = 'block';
                    }}
                  >
                    Whispers ({intimacyWhispers.length})
                  </button>
                  <button
                    className="intimacy-sub-tab"
                    data-section="compliance"
                    onClick={(e) => {
                      document.querySelectorAll('.intimacy-sub-tab').forEach(t => t.classList.remove('active'));
                      e.target.classList.add('active');
                      document.querySelectorAll('.intimacy-sub-section').forEach(s => s.style.display = 'none');
                      document.getElementById('compliance-section').style.display = 'block';
                    }}
                  >
                    Compliance Wall ({complianceWall.length})
                  </button>
                  <button
                    className="intimacy-sub-tab"
                    data-section="tasks"
                    onClick={(e) => {
                      document.querySelectorAll('.intimacy-sub-tab').forEach(t => t.classList.remove('active'));
                      e.target.classList.add('active');
                      document.querySelectorAll('.intimacy-sub-section').forEach(s => s.style.display = 'none');
                      document.getElementById('tasks-section').style.display = 'block';
                    }}
                  >
                    Tasks ({freakyTasks.length})
                  </button>
                  <button
                    className="intimacy-sub-tab"
                    data-section="protocols"
                    onClick={(e) => {
                      document.querySelectorAll('.intimacy-sub-tab').forEach(t => t.classList.remove('active'));
                      e.target.classList.add('active');
                      document.querySelectorAll('.intimacy-sub-section').forEach(s => s.style.display = 'none');
                      document.getElementById('protocols-section').style.display = 'block';
                    }}
                  >
                    Protocols ({protocols.length})
                  </button>
                  <button
                    className="intimacy-sub-tab"
                    data-section="vault"
                    onClick={(e) => {
                      document.querySelectorAll('.intimacy-sub-tab').forEach(t => t.classList.remove('active'));
                      e.target.classList.add('active');
                      document.querySelectorAll('.intimacy-sub-section').forEach(s => s.style.display = 'none');
                      document.getElementById('vault-section').style.display = 'block';
                    }}
                  >
                    Vault ({vaultContents.length})
                  </button>
                  <button
                    className="intimacy-sub-tab"
                    data-section="favor"
                    onClick={(e) => {
                      document.querySelectorAll('.intimacy-sub-tab').forEach(t => t.classList.remove('active'));
                      e.target.classList.add('active');
                      document.querySelectorAll('.intimacy-sub-section').forEach(s => s.style.display = 'none');
                      document.getElementById('favor-section').style.display = 'block';
                    }}
                  >
                    Favor Meter
                  </button>
                  <button
                    className="intimacy-sub-tab"
                    data-section="tributes"
                    onClick={(e) => {
                      document.querySelectorAll('.intimacy-sub-tab').forEach(t => t.classList.remove('active'));
                      e.target.classList.add('active');
                      document.querySelectorAll('.intimacy-sub-section').forEach(s => s.style.display = 'none');
                      document.getElementById('tributes-section').style.display = 'block';
                    }}
                  >
                    Tributes ({tributes.length})
                  </button>
                </div>

                {/* Eros Poems Section */}
                <div id="eros-poems-section" className="intimacy-sub-section" style={{ display: 'block' }}>
                  <h3>Eros Mode - Poems</h3>
                  <p className="sub-section-desc">Manage intimate poems from the Eros mode.</p>

                  <div className="content-list">
                    {intimacyPoems.length === 0 ? (
                      <p className="no-content">No intimacy poems yet</p>
                    ) : (
                      intimacyPoems.map((poem) => (
                        <div key={poem.id} className="content-item intimacy-poem-item">
                          <div className="content-info">
                            <p className="content-title">{poem.title || 'Untitled'}</p>
                            <p className="content-author">By: {poem.author || poem.createdBy || 'Unknown'}</p>
                            <p className="content-date">
                              {poem.createdAt ? new Date(poem.createdAt).toLocaleString() : 'Unknown date'}
                            </p>
                            <p className="content-preview">
                              {poem.content?.substring(0, 150)}...
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteIntimacyPoem(poem.id)}
                            disabled={loading}
                            className="delete-content-button"
                          >
                            Delete
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Eros Whispers Section */}
                <div id="eros-whispers-section" className="intimacy-sub-section" style={{ display: 'none' }}>
                  <h3>Eros Mode - Whispers</h3>
                  <p className="sub-section-desc">Manage audio whispers from the Eros mode.</p>

                  <div className="content-list">
                    {intimacyWhispers.length === 0 ? (
                      <p className="no-content">No whispers yet</p>
                    ) : (
                      intimacyWhispers.map((whisper) => (
                        <div key={whisper.id} className="content-item whisper-item">
                          <div className="content-info">
                            <p className="content-title">{whisper.title || 'Untitled Whisper'}</p>
                            <p className="content-author">By: {whisper.author || whisper.createdBy || 'Unknown'}</p>
                            <p className="content-date">
                              {whisper.createdAt ? new Date(whisper.createdAt).toLocaleString() : 'Unknown date'}
                            </p>
                            {whisper.audioData && (
                              <audio controls className="whisper-audio">
                                <source src={whisper.audioData} type="audio/webm" />
                                Your browser does not support audio.
                              </audio>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteWhisper(whisper.id)}
                            disabled={loading}
                            className="delete-content-button"
                          >
                            Delete
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Compliance Wall Section */}
                <div id="compliance-section" className="intimacy-sub-section" style={{ display: 'none' }}>
                  <h3>Freaky Mode - Compliance Wall</h3>
                  <p className="sub-section-desc">Manage images on the compliance wall background.</p>

                  <div className="compliance-actions">
                    <button
                      onClick={handleClearComplianceWall}
                      disabled={loading || complianceWall.length === 0}
                      className="danger-button"
                    >
                      Clear All
                    </button>
                    <span className="item-count">Total: {complianceWall.length} images</span>
                  </div>

                  <div className="compliance-grid">
                    {complianceWall.length === 0 ? (
                      <p className="no-content">No compliance wall images</p>
                    ) : (
                      complianceWall.map((item) => (
                        <div key={item.id} className="compliance-item">
                          <img
                            src={item.imageUrl}
                            alt={item.taskTitle || 'Compliance'}
                            className="compliance-thumbnail"
                          />
                          <div className="compliance-info">
                            <p className="compliance-task">{item.taskTitle || 'Unknown task'}</p>
                            <p className="compliance-date">
                              {item.uploadedAt ? new Date(item.uploadedAt).toLocaleDateString() : 'Unknown'}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteComplianceItem(item.id)}
                            disabled={loading}
                            className="delete-compliance-button"
                          >
                            X
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Tasks Section */}
                <div id="tasks-section" className="intimacy-sub-section" style={{ display: 'none' }}>
                  <h3>Freaky Mode - Tasks</h3>
                  <p className="sub-section-desc">Manage active and archived tasks.</p>

                  <div className="tasks-actions">
                    <button
                      onClick={handleClearAllTasks}
                      disabled={loading || freakyTasks.length === 0}
                      className="danger-button"
                    >
                      Clear Active Tasks
                    </button>
                    <button
                      onClick={handleClearArchivedTasks}
                      disabled={loading || archivedTasks.length === 0}
                      className="warning-button"
                    >
                      Clear Archived
                    </button>
                    <button
                      onClick={loadIntimacyData}
                      disabled={loading}
                      className="save-button"
                    >
                      Refresh
                    </button>
                  </div>

                  <h4>Active Tasks ({freakyTasks.length})</h4>
                  <div className="tasks-list">
                    {freakyTasks.length === 0 ? (
                      <p className="no-content">No active tasks</p>
                    ) : (
                      freakyTasks.map((task) => (
                        <div key={task.id} className={`task-item task-${task.status}`}>
                          <div className="task-info">
                            <p className="task-title">{task.title}</p>
                            <p className="task-status">Status: <strong>{task.status}</strong></p>
                            <p className="task-points">Points: {task.points} | Penalty: {task.penaltyPoints || 0}</p>
                            <p className="task-desc">{task.description?.substring(0, 100)}...</p>
                            {task.deadline && (
                              <p className="task-deadline">Deadline: {new Date(task.deadline).toLocaleString()}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            disabled={loading}
                            className="delete-task-button"
                          >
                            Delete
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  <h4>Archived Tasks ({archivedTasks.length})</h4>
                  <div className="tasks-list archived-tasks">
                    {archivedTasks.length === 0 ? (
                      <p className="no-content">No archived tasks</p>
                    ) : (
                      archivedTasks.slice(0, 10).map((task) => (
                        <div key={task.id} className={`task-item archived task-${task.status}`}>
                          <div className="task-info">
                            <p className="task-title">{task.title}</p>
                            <p className="task-status">Final Status: <strong>{task.status}</strong></p>
                            <p className="task-archived">
                              Archived: {task.archivedAt ? new Date(task.archivedAt).toLocaleDateString() : 'Unknown'}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteArchivedTask(task.id)}
                            disabled={loading}
                            className="delete-task-button"
                          >
                            Delete
                          </button>
                        </div>
                      ))
                    )}
                    {archivedTasks.length > 10 && (
                      <p className="more-items">...and {archivedTasks.length - 10} more</p>
                    )}
                  </div>
                </div>

                {/* Protocols Section */}
                <div id="protocols-section" className="intimacy-sub-section" style={{ display: 'none' }}>
                  <h3>Freaky Mode - Protocols</h3>
                  <p className="sub-section-desc">Manage the protocols/rules that Omi must accept.</p>

                  <div className="protocol-input-section">
                    <input
                      type="text"
                      value={newProtocol}
                      onChange={(e) => setNewProtocol(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddProtocol()}
                      placeholder="Enter a new protocol..."
                      className="protocol-input"
                    />
                    <button
                      onClick={handleAddProtocol}
                      disabled={loading || !newProtocol.trim()}
                      className="add-protocol-button"
                    >
                      Add Protocol
                    </button>
                    <button
                      onClick={handleClearProtocols}
                      disabled={loading || protocols.length === 0}
                      className="danger-button"
                    >
                      Clear All
                    </button>
                  </div>

                  <div className="protocols-list">
                    {protocols.length === 0 ? (
                      <p className="no-content">No protocols defined</p>
                    ) : (
                      protocols.map((protocol, index) => (
                        <div key={protocol.id} className="protocol-item">
                          <span className="protocol-number">{index + 1}.</span>
                          <span className="protocol-text">{protocol.text}</span>
                          <button
                            onClick={() => handleDeleteProtocol(protocol.id)}
                            disabled={loading}
                            className="delete-protocol-button"
                          >
                            X
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Vault Section */}
                <div id="vault-section" className="intimacy-sub-section" style={{ display: 'none' }}>
                  <h3>Freaky Mode - Vault Contents</h3>
                  <p className="sub-section-desc">Manage rewards locked in the vault (unlocked at 100 points).</p>

                  <div className="vault-add-section">
                    <h4>Add New Vault Item</h4>
                    <select
                      value={newVaultItem.type}
                      onChange={(e) => setNewVaultItem({ ...newVaultItem, type: e.target.value })}
                      className="vault-type-select"
                    >
                      <option value="text">Text Reward</option>
                      <option value="promise">Promise</option>
                      <option value="coupon">Coupon</option>
                    </select>
                    <input
                      type="text"
                      value={newVaultItem.title}
                      onChange={(e) => setNewVaultItem({ ...newVaultItem, title: e.target.value })}
                      placeholder="Title..."
                      className="vault-title-input"
                    />
                    <textarea
                      value={newVaultItem.content}
                      onChange={(e) => setNewVaultItem({ ...newVaultItem, content: e.target.value })}
                      placeholder="Content/Description..."
                      className="vault-content-input"
                    />
                    <button
                      onClick={handleAddVaultItem}
                      disabled={loading || !newVaultItem.title.trim() || !newVaultItem.content.trim()}
                      className="add-vault-button"
                    >
                      Add to Vault
                    </button>
                  </div>

                  <div className="vault-actions">
                    <button
                      onClick={handleClearVault}
                      disabled={loading || vaultContents.length === 0}
                      className="danger-button"
                    >
                      Clear Vault
                    </button>
                    <span className="item-count">Items in Vault: {vaultContents.length}</span>
                  </div>

                  <div className="vault-list">
                    {vaultContents.length === 0 ? (
                      <p className="no-content">Vault is empty</p>
                    ) : (
                      vaultContents.map((item) => (
                        <div key={item.id} className="vault-item">
                          <div className="vault-item-info">
                            <span className="vault-item-type">[{item.type}]</span>
                            <span className="vault-item-title">{item.title}</span>
                            <p className="vault-item-content">{item.content}</p>
                          </div>
                          <button
                            onClick={() => handleDeleteVaultItem(item.id)}
                            disabled={loading}
                            className="delete-vault-button"
                          >
                            Delete
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  <h4>Omi's Reward Storage ({rewardStorage.length})</h4>
                  <p className="sub-section-desc">Rewards Omi has unlocked and received.</p>
                  <div className="vault-actions">
                    <button
                      onClick={handleClearRewardStorage}
                      disabled={loading || rewardStorage.length === 0}
                      className="warning-button"
                    >
                      Clear Reward Storage
                    </button>
                  </div>
                  <div className="reward-storage-list">
                    {rewardStorage.length === 0 ? (
                      <p className="no-content">No rewards unlocked yet</p>
                    ) : (
                      rewardStorage.map((item) => (
                        <div key={item.id} className="reward-item">
                          <div className="reward-item-info">
                            <span className="reward-item-type">[{item.type}]</span>
                            <span className="reward-item-title">{item.title}</span>
                            <p className="reward-unlocked">
                              Unlocked: {item.unlockedAt ? new Date(item.unlockedAt).toLocaleDateString() : 'Unknown'}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteRewardItem(item.id)}
                            disabled={loading}
                            className="delete-reward-button"
                          >
                            Delete
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Favor Meter Section */}
                <div id="favor-section" className="intimacy-sub-section" style={{ display: 'none' }}>
                  <h3>Freaky Mode - Favor Meter</h3>
                  <p className="sub-section-desc">View and adjust Omi's current favor/points. Set below 0 to trigger punishment.</p>

                  {/* Active Punishment Warning */}
                  {activePunishment && (
                    <div className="active-punishment-warning">
                      <strong>Punishment Active:</strong> {activePunishment.text}
                      <br />
                      <small>Activated: {new Date(activePunishment.activatedAt).toLocaleString()}</small>
                    </div>
                  )}

                  <div className="favor-display">
                    <div className={`favor-meter-visual ${favorMeter < 0 ? 'negative' : ''}`}>
                      <div
                        className={`favor-fill ${favorMeter < 0 ? 'negative' : ''}`}
                        style={{ width: `${Math.max(0, favorMeter)}%` }}
                      />
                      <span className={`favor-value ${favorMeter < 0 ? 'negative' : ''}`}>{favorMeter}/100</span>
                    </div>
                    <p className={`favor-status ${favorMeter < 0 ? 'negative' : ''}`}>
                      {activePunishment
                        ? 'Punishment Active - Rewards Locked'
                        : favorMeter < 0
                          ? 'Favor in the negative - Punishment triggered!'
                          : favorMeter >= 100
                            ? 'Vault Unlocked!'
                            : `${100 - favorMeter} points until vault unlock`}
                    </p>
                  </div>

                  {!editingFavorMeter ? (
                    <button
                      onClick={() => setEditingFavorMeter(true)}
                      className="save-button"
                    >
                      Adjust Favor Meter
                    </button>
                  ) : (
                    <div className="favor-edit-section">
                      <div className="favor-slider-row">
                        <input
                          type="range"
                          min="-10"
                          max="100"
                          value={newFavorValue}
                          onChange={(e) => setNewFavorValue(parseInt(e.target.value))}
                          className={`favor-slider ${newFavorValue < 0 ? 'negative' : ''}`}
                        />
                        <input
                          type="number"
                          min="-10"
                          max="100"
                          value={newFavorValue}
                          onChange={(e) => setNewFavorValue(Math.max(-10, Math.min(100, parseInt(e.target.value) || 0)))}
                          className="favor-number-input"
                        />
                      </div>
                      <span className={`favor-new-value ${newFavorValue < 0 ? 'punishment-trigger' : ''}`}>
                        {newFavorValue < 0 ? `${newFavorValue} (Will Trigger Punishment!)` : newFavorValue}
                      </span>
                      {newFavorValue < 0 && punishments.length === 0 && (
                        <p className="punishment-warning">No punishments in pool! Add punishments via Henry's dashboard first.</p>
                      )}
                      <div className="favor-edit-buttons">
                        <button
                          onClick={handleUpdateFavorMeter}
                          disabled={loading}
                          className={newFavorValue < 0 ? 'warning-button' : 'save-button'}
                        >
                          {newFavorValue < 0 ? 'Trigger Punishment' : 'Save'}
                        </button>
                        <button
                          onClick={() => {
                            setEditingFavorMeter(false);
                            setNewFavorValue(favorMeter);
                          }}
                          className="warning-button"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tributes Section */}
                <div id="tributes-section" className="intimacy-sub-section" style={{ display: 'none' }}>
                  <h3>Freaky Mode - Tributes Archive</h3>
                  <p className="sub-section-desc">All media submissions from Omi.</p>

                  <div className="tributes-actions">
                    <button
                      onClick={handleClearTributes}
                      disabled={loading || tributes.length === 0}
                      className="danger-button"
                    >
                      Clear All Tributes
                    </button>
                    <span className="item-count">Total: {tributes.length} tributes</span>
                  </div>

                  <div className="tributes-grid">
                    {tributes.length === 0 ? (
                      <p className="no-content">No tributes yet</p>
                    ) : (
                      tributes.map((tribute) => (
                        <div key={tribute.id} className="tribute-item">
                          {tribute.mediaType?.startsWith('image') ? (
                            <img
                              src={tribute.mediaUrl}
                              alt="Tribute"
                              className="tribute-media"
                            />
                          ) : tribute.mediaType?.startsWith('video') ? (
                            <video
                              src={tribute.mediaUrl}
                              className="tribute-media"
                              controls
                            />
                          ) : tribute.mediaType?.startsWith('audio') ? (
                            <audio
                              src={tribute.mediaUrl}
                              className="tribute-audio"
                              controls
                            />
                          ) : (
                            <div className="tribute-unknown">Unknown media type</div>
                          )}
                          <div className="tribute-info">
                            <p className="tribute-filename">{tribute.fileName || 'Unknown'}</p>
                            <p className="tribute-date">
                              {tribute.submittedAt ? new Date(tribute.submittedAt).toLocaleDateString() : 'Unknown'}
                            </p>
                            <p className="tribute-by">By: {tribute.submittedBy || 'Unknown'}</p>
                          </div>
                          <button
                            onClick={() => handleDeleteTribute(tribute.id)}
                            disabled={loading}
                            className="delete-tribute-button"
                          >
                            Delete
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <motion.div
          className="confirm-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="confirm-dialog"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <p className="confirm-message">{confirmDialog.message}</p>
            <div className="confirm-buttons">
              <button className="confirm-yes" onClick={handleConfirmYes}>
                Yes
              </button>
              <button className="confirm-no" onClick={handleConfirmNo}>
                No
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Admin;
